import React from "react"
import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Rating,
  Avatar,
  IconButton,
  Alert,
} from "@mui/material"
import { Close as CloseIcon, Star as StarIcon, Person as PersonIcon } from "@mui/icons-material"
import styles from "./StaffRating.module.css"
import { set } from "date-fns"

interface StaffRatingModalProps {
  open: boolean
  onClose: () => void
  candidate: any
  project: any
  onSubmitRating: (ratingData: any) => Promise<void>
}

const StaffRatingModal: React.FC<StaffRatingModalProps> = ({ open, onClose, candidate, project, onSubmitRating }) => {
  const [rating, setRating] = useState<number | null>(0)
  const [comments, setComments] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")

  const handleSubmit = async () => {
    if (!rating || rating === 0) {
      setError("Please provide a rating")
      return
    }

    setLoading(true)
    setError("")

    try {
      const ratingData = {
        jobrole_id: candidate.jobRoleID,
        project_id: project.project_id,
        user_id: candidate.user_id,
        rating: rating,
        comment: comments.trim() || "",
      }

        await onSubmitRating(ratingData)

        setMessage("Rating submitted successfully")
        

      // Reset form and close modal
      setRating(0)
      setComments("")
        setTimeout(() => {
          onClose()
        }, 5000)
    } catch (err: any) {
      setError(err.message || "Failed to submit rating")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setRating(0)
    setComments("")
    setError("")
    setMessage("")
    onClose()
  }

  if (!candidate || !project) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth className={styles.ratingDialog}>
      <DialogTitle className={styles.dialogTitle}>
        <Box className={styles.titleHeader}>
          <Box className={styles.staffHeader}>
            <Avatar
              className={styles.staffAvatar}
              src={`/placeholder.svg?height=60&width=60&query=professional headshot of ${candidate.full_name}`}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" className={styles.staffName}>
                Rate {candidate.full_name}
              </Typography>
              <Typography variant="body2" className={styles.staffRole}>
                {candidate.jobTitle} • {project.project_name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {error && (
          <Alert severity="error" className={styles.errorAlert}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" className={styles.successAlert}>
            {message}
          </Alert>
        )}

        <Box className={styles.ratingSection}>
          <Typography variant="h6" className={styles.ratingLabel}>
            How would you rate this employee's performance?
          </Typography>
          <Box className={styles.starsContainer}>
            <Rating
              name="staff-rating"
              value={rating}
              onChange={(_, newValue) => {
                setRating(newValue)
                setError("")
              }}
              size="large"
              icon={<StarIcon fontSize="inherit" />}
              emptyIcon={<StarIcon fontSize="inherit" />}
              className={styles.starRating}
            />
            <Typography variant="body2" className={styles.ratingText}>
              {rating === 1 && "Poor"}
              {rating === 2 && "Fair"}
              {rating === 3 && "Good"}
              {rating === 4 && "Very Good"}
              {rating === 5 && "Excellent"}
              {rating === 0 && "Select a rating"}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.commentsSection}>
          <Typography variant="h6" className={styles.commentsLabel}>
            Additional Comments (Optional)
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Share your feedback about this employee's performance, work quality, communication, etc."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className={styles.commentsField}
            variant="outlined"
          />
        </Box>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button onClick={handleClose} disabled={loading} className={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !rating}
          className={styles.submitButton}
        >
          {loading ? "Submitting..." : "Submit Rating"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StaffRatingModal
